import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../contexts/LanguageContext';
import config from '../seo/config';
import routes from '../seo/routes';

const {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  OG_LOCALES,
  absoluteUrl,
} = config;
const { alternates } = routes;

const propTypes = {
  /** Page title. Keep it under ~60 characters so it is not truncated in results. */
  title: PropTypes.string.isRequired,
  /** Meta description. Aim for 150-160 characters. */
  description: PropTypes.string.isRequired,
  /** Route key from src/seo/routes.js, e.g. "home" or "moodleAgent". The canonical
      and the hreflang alternates are derived from it. */
  routeKey: PropTypes.string,
  /** Social card image (site-relative or absolute). Must be 1200x630. */
  image: PropTypes.string,
  /** Open Graph type. */
  type: PropTypes.string,
  /** Set to true to keep the page out of search results. */
  noindex: PropTypes.bool,
  /** A JSON-LD object or an array of them. Emitted as a single @graph. */
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

const defaultProps = {
  routeKey: 'home',
  image: undefined,
  type: 'website',
  noindex: false,
  jsonLd: undefined,
};

/**
 * Every page-level head tag lives here: title, description, canonical, hreflang,
 * Open Graph, Twitter and JSON-LD. Pages pass a route `key`; the canonical and
 * the alternates come from `alternates(key)`, so the English and Spanish URLs
 * always point at each other even though their slugs are different words.
 */
const Seo = ({ title, description, routeKey, image, type, noindex, jsonLd }) => {
  const { lang } = useLanguage();

  const urls = alternates(routeKey);
  const enUrl = urls.en;
  const esUrl = urls.es;
  const xDefaultUrl = urls['x-default'];
  const canonical = lang === 'es' ? esUrl : enUrl;
  const imageUrl = absoluteUrl(image || DEFAULT_OG_IMAGE);
  const locale = OG_LOCALES[lang] || OG_LOCALES.en;
  const alternateLocale = lang === 'es' ? OG_LOCALES.en : OG_LOCALES.es;

  const graph = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const jsonLdPayload = graph.length
    ? JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
    : null;

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      {/* The site-wide default (index,follow,max-image-preview:large) lives in
          public/index.html. Only emit a robots tag here when a page opts out, so
          indexable pages carry exactly one robots directive. */}
      {noindex && <meta name="robots" content="noindex,follow" />}

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="es" href={esUrl} />
      {/* x-default points at the English URL: it is the site's default language. */}
      <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={title} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={title} />

      {jsonLdPayload && <script type="application/ld+json">{jsonLdPayload}</script>}
    </Helmet>
  );
};

Seo.propTypes = propTypes;
Seo.defaultProps = defaultProps;

export default Seo;
